import {Injectable} from '@angular/core';
import {catchError, map, Observable, of, OperatorFunction, throwError} from 'rxjs';
import {GiftDetailsIdMappedInput, GiftView} from 'src/app/auth_profile/model/v3/transactions';
import {ColNativeV3Api} from './col-native-v3-api';
import {
  ApiLessonEventModel,
  ProductAvailabilityRequest,
  ScheduleDefinition,
  ScheduleReference,
  SimpleLessonScheduleRequest,
  SimpleProductAvailability,
  SimpleTeacherProductAvailability,
  StudentColLessonScheduleEvents,
  TeacherColLessonScheduleEvents,
} from '../../../model/v3/booking';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from "@angular/common/http";
import {environment} from "../../../../../environments/environment";
import {appendIfDefined, serializeDateInput} from "../../../../auth_profile/services/api/v3/rest-v3-utils";
import { Page, Pageable } from '../../../../utils/pageable';
import * as LessonsV3 from '../../../model/v3/lessons';
import { Dates } from '../../../../utils/calendar-utils';
import {
  normalizeLessonBundlesPage,
  normalizeLessonBundle,
  normalizeLessonCommitsPage,
  normalizeLessonFlagDto,
  normalizeLessonFlagsDto,
  normalizeLessonMetricDto,
  normalizeLessonsPage,
  normalizeLessonView,
  normalizeProvaContext
} from '../../../utils/v3/lessons-v3-date-utils';

@Injectable({
  providedIn: 'root'
})
export class WebColNativeV3ServiceApi
  implements ColNativeV3Api {
  constructor(private http: HttpClient) {
  }

  private studentUrl(schoolId: number, studentId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}${path}`;
  }

  private teacherUrl(schoolId: number, teacherId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/teachers/${teacherId}${path}`;
  }

  private lessonsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/lessons${path}`;
  }

  private productContextsUrl(
    schoolId: number,
    studentId: number,
    productCode?: string,
    path: string = ''
  ): string {
    const productCodePath = productCode ? `/${productCode}` : '';
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/product-contexts${productCodePath}${path}`;
  }

  private appendDateRange(params: HttpParams, dateFrom?: Date | null, dateTo?: Date | null): HttpParams {
    let next = appendIfDefined(params, 'dateFrom', serializeDateInput(dateFrom));
    next = appendIfDefined(next, 'dateTo', serializeDateInput(dateTo));
    return next;
  }

  private appendPageable(params: HttpParams, pageable?: Pageable | null): HttpParams {
    if (!pageable) {
      return params;
    }
    return pageable.appendParams(params);
  }

  private appendAll(params: HttpParams, key: string, values?: string[] | null): HttpParams {
    let next = params;
    (values ?? []).forEach((value) => {
      next = next.append(key, value);
    });
    return next;
  }

  private map404ToNull<T>(): OperatorFunction<T, T | null> {
    return catchError((error: unknown) => {
      const httpError = error instanceof HttpErrorResponse ? error : null;
      const status = httpError?.status ?? (
        typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: unknown }).status)
          : undefined
      );
      if (status === 404) {
        return of(null as T | null);
      }
      return throwError(() => (error ?? new Error('Unknown HTTP error')));
    });
  }

  private parseEventDates<T extends { events: { eventDate: unknown }[] }>(items: T[]): T[] {
    return (items ?? []).map((item) => {
      item.events?.forEach((ev) => {
        ev.eventDate = ev.eventDate instanceof Date
          ? ev.eventDate
          : Dates.parse(String(ev.eventDate));
      });
      return item;
    });
  }

  private parseScheduleEvents<T extends { events: { eventDate: unknown }[] }>(schedule: T): T {
    schedule?.events?.forEach((ev) => {
      ev.eventDate = ev.eventDate instanceof Date
        ? ev.eventDate
        : Dates.parse(String(ev.eventDate));
    });
    return schedule;
  }

  getTeacherAvailability(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleTeacherProductAvailability[]> {
    let params = this.appendDateRange(new HttpParams(), dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<SimpleTeacherProductAvailability[]>(
      this.studentUrl(schoolId, studentId, '/teacher-availability'),
      {params}
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getStudentLessonSchedules(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<StudentColLessonScheduleEvents[]> {
    let params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<StudentColLessonScheduleEvents[]>(
      this.studentUrl(schoolId, studentId, '/schedules'),
      {params}
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  reserveColLessonSchedule(
    schoolId: number,
    studentId: number,
    request: SimpleLessonScheduleRequest
  ): Observable<StudentColLessonScheduleEvents> {
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.post<StudentColLessonScheduleEvents>(
      this.studentUrl(schoolId, studentId, '/schedules'),
      request,
      {params}
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  getStudentSchedule(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<StudentColLessonScheduleEvents> {
    const params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    return this.http.get<StudentColLessonScheduleEvents>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`),
      {params}
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  updateColLessonEventData(
    schoolId: number,
    studentId: number,
    scheduleId: number,
    eventId: number,
    data: ApiLessonEventModel
  ): Observable<void> {
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.put<void>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}/events/${eventId}/data`),
      data,
      {params}
    );
  }

  deleteColLesson(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<void> {
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.delete<void>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`),
      {params}
    );
  }

  getAvailabilityEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleProductAvailability[]> {
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<SimpleProductAvailability[]>(
      this.teacherUrl(schoolId, teacherId, '/schedule-events'),
      {params}
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherColLessonEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null
  ): Observable<TeacherColLessonScheduleEvents[]> {
    let params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    return this.http.get<TeacherColLessonScheduleEvents[]>(
      this.teacherUrl(schoolId, teacherId, '/schedule-events'),
      {params}
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherCourseProfiles(
    schoolId: number,
    teacherId: number
  ): Observable<LessonsV3.V3CourseTeacherProfile[]> {
    return this.http.get<LessonsV3.V3CourseTeacherProfile[]>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/teachers/${teacherId}/course-profiles`
    );
  }

  createAvailability(
    schoolId: number,
    teacherId: number,
    request: ProductAvailabilityRequest
  ): Observable<ScheduleReference> {
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    return this.http.post<ScheduleReference>(
      this.teacherUrl(schoolId, teacherId, '/schedules'),
      request,
      {params}
    );
  }

  deleteAvailability(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`),
      {params}
    );
  }

  deleteTeacherColLesson(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`),
      {params}
    );
  }

  deleteAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    deleteAll?: boolean | null
  ): Observable<void> {
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'deleteAll', deleteAll);
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`),
      {params}
    );
  }

  updateAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    definition: ScheduleDefinition,
    applyFuture?: boolean | null
  ): Observable<ScheduleDefinition> {
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'applyFuture', applyFuture);
    return this.http.put<ScheduleDefinition>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`),
      definition,
      {params}
    );
  }

  getStudentGifts(
    schoolId: number,
    studentId: number,
    unused?: boolean | null,
    platform?: string | null,
  ): Observable<GiftView[]> {
    let params = appendIfDefined(new HttpParams(), 'unused', unused);
    params = appendIfDefined(params, 'platform', platform);
    return this.http.get<GiftView[]>(`${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/gifts`, {
      params,
    });
  }

  createStudentGift(
    schoolId: number,
    studentId: number,
    body: GiftDetailsIdMappedInput,
    platform: string | null,
  ): Observable<GiftDetailsIdMappedInput> {
    const params = appendIfDefined(new HttpParams(), 'platform', platform);
    return this.http.post<GiftDetailsIdMappedInput>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/gifts`,
      body,
      {params},
    );
  }

  findLessons(
    type?: LessonsV3.LessonCategory | null,
    lessonType?: LessonsV3.LessonType | null,
    view?: string | null,
    eid?: string | null,
    studentId?: number | null,
    teacherId?: number | null,
    groupId?: number | null,
    groupEid?: string | null,
    productCode?: string | null,
    statuses?: LessonsV3.LessonStatus[] | null,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.LessonView>> {
    const normalizedProductCode = productCode?.trim() ? productCode.trim() : null;
    let params = new HttpParams();
    params = appendIfDefined(params, 'type', type);
    params = appendIfDefined(params, 'lessonType', lessonType);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'eid', eid);
    params = appendIfDefined(params, 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    params = appendIfDefined(params, 'groupId', groupId);
    params = appendIfDefined(params, 'groupEid', groupEid);
    params = appendIfDefined(params, 'productCode', normalizedProductCode);
    params = this.appendAll(params, 'statuses', statuses ?? null);
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = this.appendPageable(params, pageable);
    return this.http
      .get<Page<LessonsV3.LessonView>>(this.lessonsUrl(), { params })
      .pipe(map((page) => normalizeLessonsPage(page)));
  }

  getLesson(
    lessonId: number,
    view?: string | null,
    viewStudentId?: number | null
  ): Observable<LessonsV3.LessonView> {
    let params = appendIfDefined(new HttpParams(), 'view', view);
    params = appendIfDefined(params, 'viewStudentId', viewStudentId);
    return this.http
      .get<LessonsV3.LessonView>(this.lessonsUrl(`/${lessonId}`), { params })
      .pipe(map((lesson) => normalizeLessonView(lesson)));
  }

  getLessonMetric(lessonId: number): Observable<LessonsV3.V3LessonMetric> {
    return this.http
      .get<LessonsV3.V3LessonMetric>(this.lessonsUrl(`/${lessonId}/metric`))
      .pipe(map((metric) => normalizeLessonMetricDto(metric)));
  }

  sendLessonNotification(lessonId: number): Observable<void> {
    return this.http.post<void>(this.lessonsUrl(`/${lessonId}/notifications`), null);
  }

  getLessonComment(
    lessonId: number,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    return this.http.get<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), { params });
  }

  updateLessonComments(
    lessonId: number,
    message: LessonsV3.LessonMessageDto,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    return this.http.put<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), message, { params });
  }

  getLessonFlags(lessonId: number): Observable<LessonsV3.ApiLessonFlag[]> {
    return this.http
      .get<LessonsV3.ApiLessonFlag[]>(this.lessonsUrl(`/${lessonId}/flags`))
      .pipe(map((flags) => normalizeLessonFlagsDto(flags)));
  }

  createLessonFlag(
    lessonId: number,
    details: LessonsV3.LessonFlagDto,
    studentId?: number | null,
    teacherId?: number | null
  ): Observable<LessonsV3.ApiLessonFlag> {
    let params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    return this.http
      .post<LessonsV3.ApiLessonFlag>(this.lessonsUrl(`/${lessonId}/flags`), details, { params })
      .pipe(map((flag) => normalizeLessonFlagDto(flag)));
  }

  deleteLessonFlag(lessonId: number, flagId: number): Observable<void> {
    return this.http.delete<void>(this.lessonsUrl(`/${lessonId}/flags/${flagId}`));
  }

  getLessonGroup(lessonId: number): Observable<LessonsV3.LessonGroupParticipant> {
    return this.http.get<LessonsV3.LessonGroupParticipant>(this.lessonsUrl(`/${lessonId}/group`));
  }

  getLessonTeacher(
    lessonId: number
  ): Observable<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>> {
    return this.http.get<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>>(this.lessonsUrl(`/${lessonId}/teacher`));
  }

  getLessonStudents(
    lessonId: number
  ): Observable<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]> {
    return this.http.get<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]>(this.lessonsUrl(`/${lessonId}/students`));
  }

  getLessonStudentProgress(
    lessonId: number,
    studentNumber: number
  ): Observable<LessonsV3.V3LessonParticipantProgressDetails> {
    return this.http.get<LessonsV3.V3LessonParticipantProgressDetails>(this.lessonsUrl(`/${lessonId}/students/${studentNumber}/progress`));
  }

  getLessonNotes(lessonId: number): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(this.lessonsUrl(`/${lessonId}/notes`));
  }

  getLessonProgress(lessonId: number): Observable<LessonsV3.LessonProgressDetails> {
    return this.http.get<LessonsV3.LessonProgressDetails>(this.lessonsUrl(`/${lessonId}/progress`));
  }

  updateLessonProgress(
    lessonId: number,
    progress: LessonsV3.LessonProgressDetailsDto,
    type?: LessonsV3.LessonProgressType | null
  ): Observable<LessonsV3.LessonProgressDetails> {
    const params = appendIfDefined(new HttpParams(), 'type', type);
    return this.http.put<LessonsV3.LessonProgressDetails>(this.lessonsUrl(`/${lessonId}/progress`), progress, { params });
  }

  updateLessonType(
    lessonId: number,
    type: LessonsV3.LessonType
  ): Observable<void> {
    return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/type`), JSON.stringify(type), {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    });
  }

  updateLessonStatus(
    lessonId: number,
    actionOrStatus: LessonsV3.LessonStatusAction | LessonsV3.LessonStatus,
    request?: LessonsV3.LessonStatusRequestDto | null,
    studentId?: number | null,
    teacherId?: number | null,
    checkStatus?: boolean | null
  ): Observable<LessonsV3.LessonView | void> {
    const isAction = Object.values(LessonsV3.LessonStatusAction).includes(actionOrStatus as LessonsV3.LessonStatusAction);
    if (isAction) {
      let params = appendIfDefined(new HttpParams(), 'action', actionOrStatus);
      params = appendIfDefined(params, 'studentId', studentId);
      params = appendIfDefined(params, 'teacherId', teacherId);
      params = appendIfDefined(params, 'checkStatus', checkStatus);
      return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/status`), request, { params });
    }
    return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/status`), JSON.stringify(actionOrStatus), {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    });
  }

  getPersonColSettings(personId: number): Observable<LessonsV3.PersonalSettings> {
    return this.http.get<LessonsV3.PersonalSettings>(
      `${environment.eventsApiBase}/v1/people/${personId}/col-settings`
    );
  }

  updatePersonColSettings(
    personId: number,
    settings: LessonsV3.PersonalSettings
  ): Observable<LessonsV3.PersonalSettings> {
    return this.http.put<LessonsV3.PersonalSettings>(
      `${environment.eventsApiBase}/v1/people/${personId}/col-settings`,
      settings
    );
  }

  updatePersonIntroductionState(
    personId: number,
    state: LessonsV3.IntroductionState
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.eventsApiBase}/v1/people/${personId}/col-settings/introduction-state`,
      state
    );
  }

  getStudentBundles(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    available?: boolean | null
  ): Observable<Page<LessonsV3.LessonBundle>> {
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'available', available);
    return this.http.get<Page<LessonsV3.LessonBundle>>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/bundles`,
      { params }
    ).pipe(map((page) => normalizeLessonBundlesPage(page)));
  }

  createStudentBundle(
    schoolId: number,
    studentId: number,
    details: LessonsV3.LessonBundleDetails
  ): Observable<LessonsV3.LessonBundle> {
    return this.http.post<LessonsV3.LessonBundle>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/bundles`,
      details
    ).pipe(map((bundle) => normalizeLessonBundle(bundle)));
  }

  updateStudentBundleExpiry(
    schoolId: number,
    studentId: number,
    bundleId: number,
    expiryDate: Date
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/bundles/${bundleId}/expiry-date`,
      expiryDate
    );
  }

  getStudentCommits(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    courseCode?: string | null,
    commitStatuses?: LessonsV3.LessonCommitStatus[] | null,
    lessonStatuses?: LessonsV3.LessonStatus[] | null
  ): Observable<Page<LessonsV3.LessonCommit>> {
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'courseCode', courseCode);
    params = this.appendAll(params, 'commitStatuses', commitStatuses ?? null);
    params = this.appendAll(params, 'lessonStatuses', lessonStatuses ?? null);
    return this.http.get<Page<LessonsV3.LessonCommit>>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/students/${studentId}/commits`,
      { params }
    ).pipe(map((page) => normalizeLessonCommitsPage(page)));
  }

  getStudents(
    schoolId?: number | null,
    personId?: number | null,
    email?: string | null,
    studentIds?: number[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.V3SchoolStudent>> {
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'schoolId', schoolId);
    params = appendIfDefined(params, 'personId', personId);
    params = appendIfDefined(params, 'email', email);
    params = this.appendAll(params, 'studentIds', (studentIds ?? []).map(String));
    return this.http.get<Page<LessonsV3.V3SchoolStudent>>(
      `${environment.eventsApiBase}/v1/students`,
      { params }
    );
  }

  getSchoolProducts(schoolId: number): Observable<LessonsV3.V3Product[]> {
    return this.http.get<LessonsV3.V3Product[]>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/products`
    );
  }

  getProductVariants(
    view?: string | null,
    platform?: string | null,
    variantCodes?: string[] | null,
    lang?: string | null,
    currency?: string | null,
    priceLists?: string[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.ProductVariantPreview>> {
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'platform', platform);
    params = this.appendAll(params, 'variantCode', variantCodes ?? null);
    params = appendIfDefined(params, 'lang', lang);
    params = appendIfDefined(params, 'currency', currency);
    params = this.appendAll(params, 'priceList', priceLists ?? null);
    return this.http.get<Page<LessonsV3.ProductVariantPreview>>(
      `${environment.eventsApiBase}/v1/product-variants`,
      { params }
    );
  }

  getProductCourses(
    schoolId: number,
    productCode: string
  ): Observable<LessonsV3.Course[]> {
    return this.http.get<LessonsV3.Course[]>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/products/${productCode}/courses`
    ).pipe(this.map404ToNull<LessonsV3.Course[]>());
  }

  getCourseCompetences(
    schoolId: number,
    productCode: string,
    courseCode: string
  ): Observable<LessonsV3.Competence[]> {
    return this.http.get<LessonsV3.Competence[]>(
      `${environment.eventsApiBase}/v1/schools/${schoolId}/products/${productCode}/courses/${courseCode}/competences`
    );
  }

  getCompetenceMappings(): Observable<LessonsV3.CompetenceMapping[]> {
    return this.http.get<LessonsV3.CompetenceMapping[]>(
      `${environment.eventsApiBase}/v1/products/competence-mappings`
    );
  }

  getProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string
  ): Observable<LessonsV3.ColProvaContextDetails | null> {
    return this.http.get<LessonsV3.ColProvaContextDetails>(
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context')
    ).pipe(
      this.map404ToNull<LessonsV3.ColProvaContextDetails>(),
      map((context) => normalizeProvaContext(context))
    );
  }

  updateProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ColProvaContextDetails
  ): Observable<LessonsV3.ColProvaContextDetails> {
    return this.http.put<LessonsV3.ColProvaContextDetails>(
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context'),
      context
    ).pipe(map((updated) => normalizeProvaContext(updated)));
  }

  getProductContexts(
    schoolId: number,
    studentId: number,
    productCode?: string | null
  ): Observable<LessonsV3.ProductContextDetails[] | null> {
    const params = appendIfDefined(new HttpParams(), 'productCode', productCode);
    return this.http.get<LessonsV3.ProductContextDetails[]>(
      this.productContextsUrl(schoolId, studentId),
      { params }
    ).pipe(this.map404ToNull<LessonsV3.ProductContextDetails[]>());
  }

  updateProductContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ProductContextDetails
  ): Observable<void> {
    return this.http.put<void>(this.productContextsUrl(schoolId, studentId, productCode), context);
  }

  getCourseContexts(
    schoolId: number,
    studentId: number,
    productCode: string,
    current?: boolean | null
  ): Observable<LessonsV3.ColCourseContext[] | []> {
    const params = appendIfDefined(new HttpParams(), 'current', current);
    return this.http.get<LessonsV3.ColCourseContext[]>(
      this.productContextsUrl(schoolId, studentId, productCode, '/course-contexts'),
      { params }
    ).pipe(this.map404ToNull<LessonsV3.ColCourseContext[]>());
  }
}
